import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, setAuthToken, getAuthToken } from '../api/client';
import { connect, disconnect, getSocket } from '../services/socket';

export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // For initial app load only
  const [isAuthenticating, setIsAuthenticating] = useState(false); // For login/register operations
  const [error, setError] = useState(null);

  // Initialize auth state on app load
  useEffect(() => {
    const initAuth = async () => {
      const token = getAuthToken();
      if (token) {
        try {
          const response = await authAPI.getMe();
          setUser(response.data.user);

          // Proactively refresh if the token expires within 24 hours
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            const secondsUntilExpiry = payload.exp - Math.floor(Date.now() / 1000);
            if (secondsUntilExpiry < 86400) {
              const refreshRes = await authAPI.refresh();
              setAuthToken(refreshRes.data.token);
            }
          } catch {
            // Non-fatal — skip proactive refresh if decode fails
          }
        } catch (error) {
          console.error('Failed to verify token:', error);
          setAuthToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Manage socket connection based on auth state
  useEffect(() => {
    if (user) {
      // User is logged in, ensure socket is connected
      // Wait a small delay to ensure token is set in localStorage
      const timer = setTimeout(() => {
        console.log('🔐 AuthContext: User authenticated, connecting socket');
        try {
          connect();
        } catch (error) {
          console.error('❌ Failed to connect socket:', error);
          setError('Failed to establish real-time connection');
        }
      }, 100);
      
      return () => clearTimeout(timer);
    } else {
      // User is logged out, disconnect socket
      console.log('🔐 AuthContext: User logged out, disconnecting socket');
      disconnect();
    }
  }, [user]);

  const login = async (email, password, turnstileToken) => {
    setIsAuthenticating(true); // Use isAuthenticating instead of loading
    setError(null);

    try {
      const response = await authAPI.login({ email, password, turnstileToken });
      const { token, user: userData } = response.data;
      
      setAuthToken(token);
      setUser(userData);
      
      // Socket connection will be handled by the useEffect watching user state
      console.log('🔐 AuthContext: Login successful, socket will connect via useEffect');
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Login failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsAuthenticating(false);
    }
  };

  const register = async (name, email, password, role = 'USER', licenseNumber = null, phone = '', specialization = '', turnstileToken) => {
    setIsAuthenticating(true);
    setError(null);

    try {
      const registerData = { name, email, password, role, turnstileToken };
      if (role === 'DOCTOR' && licenseNumber) registerData.licenseNumber = licenseNumber;
      if (role === 'DOCTOR' && specialization) registerData.specialization = specialization;
      if (phone) registerData.phone = phone;

      const response = await authAPI.register(registerData);
      const { token, user: userData } = response.data;
      
      // Doctors are PENDING — store token but don't set user (they can't use the app yet)
      if (role === 'DOCTOR') {
        // Don't auto-login: let Register.jsx show the pending screen
        return { success: true };
      }

      setAuthToken(token);
      setUser(userData);
      
      console.log('🔐 AuthContext: Registration successful, socket will connect via useEffect');
      
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Registration failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsAuthenticating(false);
    }
  };

  const logout = () => {
    // Disconnect WebSocket
    disconnect();
    
    setAuthToken(null);
    setUser(null);
    setError(null);
  };

  const clearError = () => setError(null);

  const value = {
    user,
    setUser,
    loading,
    isAuthenticating,
    error,
    login,
    register,
    logout,
    clearError,
    isAuthenticated: !!user,
    isDoctor: user?.role === 'DOCTOR',
    isUser: user?.role === 'USER',
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};